import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import {
  CopyableHash,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  InvoiceDetailSkeleton,
} from '@shared/ui';
import type { Invoice, InvoiceTransaction } from '@entities/invoice';
import { getInvoicePayLink } from '@entities/invoice';
import { formatCryptoAmount } from '@shared/lib/format-amount';
import { formatDateTime, formatTableDateTime } from '@shared/lib/format-date';
import { useCopyToClipboard } from '@shared/lib/use-copy-to-clipboard';
import { InvoiceStatusBadge } from '@entities/invoice';
import { TransactionAmlBadge } from '@entities/invoice';
import { TransactionAmlModal } from './transaction-aml-modal';

interface InvoiceInfoModalProps {
  invoice: Invoice | null;
  isLoading?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InvoiceInfoModal({
  invoice,
  isLoading = false,
  open,
  onOpenChange,
}: InvoiceInfoModalProps) {
  const { t } = useTranslation('cabinet');
  const { copy } = useCopyToClipboard();
  const [transactionsOpen, setTransactionsOpen] = useState(true);
  const [amlTransaction, setAmlTransaction] = useState<InvoiceTransaction | null>(null);

  const payLink = invoice ? getInvoicePayLink(invoice.id) : '';
  const shortCode = invoice?.currency.shortCode ?? '';
  const invoiceAmount = invoice?.totalAmount ?? invoice?.amount ?? 0;

  const showAmlColumn = useMemo(
    () => Boolean(invoice?.transactions.some((transaction) => transaction.aml != null)),
    [invoice?.transactions],
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('payments.modal.title')}</DialogTitle>
          </DialogHeader>

          {isLoading || !invoice ? (
            <InvoiceDetailSkeleton />
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 items-start gap-y-1 text-sm [&>span]:mt-3 [&>span:first-child]:mt-0 sm:grid-cols-[minmax(120px,175px)_1fr] sm:gap-y-2 sm:[&>span]:mt-0">
                <span className="text-muted-foreground">{t('payments.modal.id')}</span>
                <p className="break-all font-semibold">{invoice.id}</p>

                <span className="text-muted-foreground">{t('payments.modal.link')}</span>
                <a
                  href={payLink}
                  target="_blank"
                  rel="noreferrer"
                  className="break-all font-semibold underline"
                >
                  {payLink}
                </a>

                <span className="text-muted-foreground">{t('payments.modal.amount')}</span>
                <div className="flex items-center gap-1">
                  <span>
                    {formatCryptoAmount(invoiceAmount, shortCode, true)} {shortCode}
                  </span>
                  {invoice.currency.iconBase && (
                    <img
                      src={invoice.currency.iconBase}
                      alt={shortCode}
                      className="h-6 w-6"
                    />
                  )}
                </div>

                <span className="text-muted-foreground">{t('payments.modal.payed')}</span>
                <div className="flex items-center gap-1">
                  <span>
                    {formatCryptoAmount(invoice.paidAmount, shortCode, true)} {shortCode}
                  </span>
                  {invoice.currency.iconBase && (
                    <img
                      src={invoice.currency.iconBase}
                      alt={shortCode}
                      className="h-6 w-6"
                    />
                  )}
                </div>

                <span className="text-muted-foreground">
                  {t('payments.modal.transferCommission')}
                </span>
                <p className="break-all font-semibold">{invoice.commissionTransfer ?? '-'}</p>

                <span className="text-muted-foreground">
                  {t('payments.modal.serviceCommission')}
                </span>
                <p className="break-all font-semibold">{invoice.commissionService ?? '-'}</p>

                <span className="text-muted-foreground">{t('payments.modal.type')}</span>
                <p>-</p>

                <span className="text-muted-foreground">{t('payments.modal.status')}</span>
                <div>
                  <InvoiceStatusBadge status={invoice.status} />
                </div>

                <span className="text-muted-foreground">{t('payments.modal.address')}</span>
                <p className="break-all font-semibold">{invoice.address?.address ?? '-'}</p>

                <span className="text-muted-foreground">{t('payments.modal.fixedCourse')}</span>
                <p>{t('common.yes', { ns: 'common' })}</p>

                <span className="text-muted-foreground">{t('payments.modal.userCommission')}</span>
                <p>{t('common.no', { ns: 'common' })}</p>

                <span className="text-muted-foreground">{t('payments.modal.created')}</span>
                <p>{formatDateTime(invoice.createdAt)}</p>

                <span className="text-muted-foreground">{t('payments.modal.expires')}</span>
                <p>{invoice.expiresAt ? formatDateTime(invoice.expiresAt) : '-'}</p>
              </div>

              {invoice.transactions.length > 0 && (
                <div className="rounded-2xl border border-border">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium"
                    onClick={() => setTransactionsOpen((prev) => !prev)}
                  >
                    {t('payments.modal.transactions')}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${transactionsOpen ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {transactionsOpen && (
                    <div className="-mx-1 min-w-0 overflow-x-auto px-1 pb-3">
                      <table className="w-full min-w-[420px] table-fixed text-xs">
                        <colgroup>
                          <col style={{ width: showAmlColumn ? '19%' : '22%' }} />
                          <col style={{ width: showAmlColumn ? '26%' : '30%' }} />
                          <col style={{ width: showAmlColumn ? '47%' : '48%' }} />
                          {showAmlColumn && <col style={{ width: '8%' }} />}
                        </colgroup>
                        <thead>
                          <tr>
                            <th className="px-2 py-1.5 text-left text-[11px] font-medium text-muted-foreground">
                              {t('payments.modal.date')}
                            </th>
                            <th className="px-2 py-1.5 text-left text-[11px] font-medium text-muted-foreground">
                              {t('payments.modal.amount')}
                            </th>
                            <th className="px-2 py-1.5 text-left text-[11px] font-medium text-muted-foreground">
                              {t('payments.modal.txid')}
                            </th>
                            {showAmlColumn && (
                              <th className="px-1 py-1.5 text-left text-[11px] font-medium text-muted-foreground">
                                {t('payments.aml.column')}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {invoice.transactions.map((transaction) => (
                            <tr key={transaction.txid} className="hover:bg-secondary/50">
                              <td className="px-2 py-1.5 whitespace-nowrap tabular-nums">
                                {formatTableDateTime(transaction.time)}
                              </td>
                              <td className="px-2 py-1.5">
                                <span className="block truncate tabular-nums">
                                  {formatCryptoAmount(transaction.amount, transaction.asset, true)}{' '}
                                  {transaction.asset}
                                </span>
                                {transaction.network && (
                                  <span className="block truncate text-[10px] text-muted-foreground">
                                    {transaction.network}
                                  </span>
                                )}
                              </td>
                              <td className="min-w-0 px-2 py-1.5">
                                <CopyableHash
                                  value={transaction.txid}
                                  onCopy={() => copy(transaction.txid)}
                                />
                              </td>
                              {showAmlColumn && (
                                <td className="px-1 py-1.5">
                                  <TransactionAmlBadge
                                    aml={transaction.aml}
                                    onClick={() => setAmlTransaction(transaction)}
                                    className="px-1.5 py-0 text-[10px]"
                                  />
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {invoice.address?.qr && (
                <div className="flex flex-col items-center gap-2">
                  <h2 className="font-bold">{t('payments.modal.qr')}</h2>
                  <img
                    src={invoice.address.qr}
                    alt="QR"
                    className="aspect-square h-auto w-full max-w-[min(270px,60vw)] object-contain"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TransactionAmlModal
        transaction={amlTransaction}
        open={Boolean(amlTransaction)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setAmlTransaction(null);
        }}
      />
    </>
  );
}
